import React, { useEffect, useRef, useState } from 'react'

const Cta = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [hoveredButton, setHoveredButton] = useState<string | null>(null)
  const sectionRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
        }
      },
      { threshold: 0.1 }
    )

    if (sectionRef.current) {
      observer.observe(sectionRef.current)
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current)
      }
    }
  }, [])

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId.replace('#', ''))
    if (element) {
      const offset = 100
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.scrollY - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    }
  }

  return (
    <section
      ref={sectionRef}
      className="relative py-24 lg:py-32 overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #596856 0%, #A8B8A3 100%)',
      }}
    >
      {/* Animated background patterns */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" />
        <div
          className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: '2s' }}
        />
      </div>

      {/* Decorative geometric shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className={`absolute top-10 left-10 w-20 h-20 border-2 border-white/20 rounded-lg transform rotate-12 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
          }`}
        />
        <div
          className={`absolute bottom-10 right-10 w-32 h-32 border-2 border-white/20 rounded-full transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
          }`}
        />
        <div
          className={`absolute top-1/2 left-20 w-16 h-16 border-2 border-white/10 rounded-lg transform -rotate-6 transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
          }`}
        />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Animated badge */}
          <div
            className={`inline-block mb-8 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'
            }`}
          ></div>

          {/* Main heading with split animation */}
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-white mb-6 leading-tight">
            <span
              className={`block transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              Ready to Transform
            </span>
            <span
              className={`block text-[#F4F1ED] transition-all duration-700 delay-100 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              Your Space?
            </span>
          </h2>

          {/* Enhanced subtitle */}
          <p
            className={`text-xl md:text-2xl text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            Join the hundreds of families and businesses who've discovered the life-changing magic
            of
            <span className="text-[#D5B8A8] font-medium"> professional organization</span>.
          </p>

  

          {/* CTA Buttons with enhanced hover effects */}
          <div
            className={`flex flex-col sm:flex-row gap-5 justify-center items-center transition-all duration-700 delay-400 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <button
              onClick={() => scrollToSection('contact')}
              onMouseEnter={() => setHoveredButton('primary')}
              onMouseLeave={() => setHoveredButton(null)}
              className="group relative px-10 py-4 bg-[#D5B8A8] hover:bg-[#c5a798] text-white font-semibold 
                       rounded-xl shadow-2xl transform transition-all duration-300 hover:scale-105 hover:-translate-y-1
                       focus:outline-none focus:ring-4 focus:ring-[#D5B8A8]/50"
              aria-label="Book your consultation"
            >
              <span className="relative z-10 flex items-center text-lg">
                Free Consultation
                <svg
                  className={`w-5 h-5 ml-3 transition-transform duration-300 ${
                    hoveredButton === 'primary' ? 'translate-x-1' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </span>

              {/* Animated gradient overlay */}
              <div
                className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent 
                          rounded-xl transition-transform duration-700 ${
                            hoveredButton === 'primary' ? 'translate-x-full' : '-translate-x-full'
                          }`}
              />
            </button>

            <button
              onClick={() => scrollToSection('services')}
              onMouseEnter={() => setHoveredButton('secondary')}
              onMouseLeave={() => setHoveredButton(null)}
              className="group relative px-10 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold 
                       rounded-xl border-2 border-white/30 hover:border-white/50 hover:bg-white/20
                       shadow-xl transform transition-all duration-300 hover:scale-105 hover:-translate-y-1
                       focus:outline-none focus:ring-4 focus:ring-white/30"
              aria-label="View our services"
            >
              <span className="relative z-10 flex items-center text-lg">
                Explore Our Services
                <svg
                  className={`w-5 h-5 ml-3 transition-all duration-300 ${
                    hoveredButton === 'secondary'
                      ? 'opacity-100 translate-x-1'
                      : 'opacity-0 -translate-x-2'
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </span>
            </button>
          </div>

          {/* Urgency/Trust indicator */}
          <div
            className={`mt-10 transition-all duration-700 delay-500 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="inline-flex items-center space-x-6 text-white/80 text-sm">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-[#D5B8A8]" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>No obligation</span>
              </div>
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-[#D5B8A8]" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Free consultation</span>
              </div>
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-[#D5B8A8]" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Instant booking</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CSS for animations */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `,
        }}
      />
    </section>
  )
}

export default Cta
